import React, { Fragment, useMemo, useState, useRef } from 'react';
import { RiRefreshLine, RiAddBoxLine, RiEditLine } from 'react-icons/ri';
import { Button, DatePicker, Input, Space, Table } from 'antd';
import { green, yellow, blue } from '@ant-design/colors';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useFetch } from '../../hooks/useFetch';

export default ({ title, columns, actionPosition, url, editPath, createPath, HeaderComponent, otherAction, refresh, conditionalRows,
    ExpandComponent, searchComponent, expandableComponent, scroll
}) => {
    const [search2, setSearch2] = useState({});
    const history = useHistory();
    const [page, setPage] = useState(1);
    const perPage = 10;
    const searchInput = useRef(null);
    const [searchedColumn, setSearchedColumn] = useState('');
    const [searchText, setSearchText] = useState('');

    const handleSearch = (dataIndex, selectedKeys) => {
        setSearch2((prev) => ({
            ...prev,
            [dataIndex]: selectedKeys[0],
        }));
    };

    const humanize = (text) =>
        text
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());

    const getColumnSearchProps = (title, typesearch, dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                {typesearch === 'date' ? (
                    <DatePicker
                        format="DD-MMM-YYYY"
                        value={
                            selectedKeys[0]
                                ? moment(selectedKeys[0], 'DD-MMM-YYYY')
                                : null
                        }
                        onChange={(date) => {
                            if (date) {
                                setSelectedKeys([date.format('DD-MMM-YYYY')]);
                            } else {
                                setSelectedKeys([]);
                            }
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                ) : (
                    <Input
                        placeholder={`Search ${humanize(dataIndex.toString())}`}
                        value={selectedKeys[0]}
                        onChange={(e) =>
                            setSelectedKeys(e.target.value ? [e.target.value] : [])
                        }
                        onPressEnter={() => handleSearch(dataIndex, selectedKeys)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                )}
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(dataIndex, selectedKeys)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            if (clearFilters) {
                                clearFilters();
                                setSearch2((prev) => {
                                    const updated = { ...prev };
                                    delete updated[dataIndex];
                                    return updated;
                                });
                            }
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={close}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[title]
                ?.toString()
                .toLowerCase()
                .includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible && typesearch !== 'date') {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === title ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const modifiedColumns = columns.map((column) => {
        if (column.search) {
            return {
                ...column,
                ...getColumnSearchProps(column.title, column.typesearch, column.dataIndex),
            };
        }
        return column;
    });

    if (!!editPath) {
        modifiedColumns.push({
            key: 'edit',
            title: 'Edit',
            dataIndex: 'Edit',
            render: (_, rows) => (
                <>
                    <Button
                        icon={<RiEditLine size={15} color={green[7]} />}
                        type="text"
                        onClick={() => history.push({ pathname: editPath, state: { ...rows, isCreate: false } })}
                    />
                </>
            ),
        });
    }

    const [localRefresh, setLocalRefresh] = useState(moment().unix())
    const uri = useMemo(() => {
        const parentRefresh = parseInt(refresh) || 0;
        const r = localRefresh > parentRefresh ? localRefresh : parentRefresh;
        return `${url}?page=${page}&perPage=${perPage}&search2=${JSON.stringify(search2)}&timestamp=${r}`;
    }, [url, refresh, localRefresh, search2, page, perPage])
    const [data, loading] = useFetch(uri);

    const handlePaginationChange = (pagination) => {
        setPage(pagination)
    }

    return (
        <Fragment>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginBlock: 10 }}>
                <Button
                    icon={<RiRefreshLine size={15} color={yellow[7]} />}
                    style={{ margin: 2 }}
                    title='Refresh'
                    onClick={() => setLocalRefresh(moment().unix())}
                />
                {
                    !!createPath && <Button
                        icon={<RiAddBoxLine size={15} color={blue[7]} />}
                        title='Add'
                        style={{ margin: 2 }}
                        onClick={() => history.push({ pathname: createPath, state: { isCreate: true } })}
                    />
                }
            </div>
            <Table
                columns={modifiedColumns}
                expandable={expandableComponent || null}
                expandedRowRender={ExpandComponent}
                className='small-table'
                dataSource={data?.data || []}
                size="small"
                rowKey="_id"
                loading={loading}
                scroll={scroll}
                pagination={{
                    total: data?.total || 0,
                    pageSize: perPage,
                    onChange: handlePaginationChange,
                    position: ['bottomRight'],
                }}
            />
        </Fragment>
    )
}